import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField } from '../base-field';
import { Checkbox } from '../checkbox';
import { FieldLabel } from '../field-label';

import type { ForwardedRef, ReactElement } from 'react';

import type { CheckboxProps } from '../checkbox';
import type { CheckboxFieldProps } from './checkbox-field.types';

import './checkbox-field.scss';

/**
 * Checkbox fields are used to pick an option when one or more options can be chosen.
 * A single checkbox field can be used representing a singular option,
 * for the purpose of toggling an element between an active and inactive state.
 *
 * ### Usage
 *
 * ```tsx
 * import { useState } from 'react';
 * import { CheckboxField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   const [checked, setChecked] = useState(true);
 *
 *   return <CheckboxField checked={checked} onChange((event) => setChecked(event.target.checked)) />
 * }
 * ```
 */

const CheckboxField = forwardRef(
  (props: CheckboxFieldProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
    const {
      checked,
      className,
      defaultChecked,
      disabled,
      indeterminate,
      inputProps,
      label,
      onChange,
      readOnly,
      required,
      tooltip,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const controlId = useFieldId(inputProps?.id, 'rbui-checkbox-field');

    const checkboxProps: CheckboxProps = {
      checked,
      defaultChecked,
      disabled,
      indeterminate,
      inputProps: {
        ...inputProps,
        id: controlId,
      },
      onChange,
      readOnly,
      required,
    };

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip} view="normal">
        {label}
      </FieldLabel>
    );

    return (
      <BaseField
        className={cn('rbui-checkbox-field', className)}
        control={<Checkbox {...checkboxProps} ref={forwardedRef} />}
        label={fieldLabel}
        labelPosition="inline-end"
        {...rest}
      />
    );
  },
);

CheckboxField.displayName = 'CheckboxField';

export default CheckboxField;
